import { removeSubscriberFromProjectQuery } from '@common/queries/team';
import { Entity } from '@common/types/Entity';
import { EntityEvents } from '@common/user-analytics';
import { theme } from '@prophecy/theme';
import { Dialog, Stack, Text } from '@prophecy/ui-v3';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { useMixPanel } from '../../../../../context/mixpanel/context';
import { ReactQueryError, useGraphQlMutation } from '../../../../../data/util';

const TeamName = styled.strong`
  padding: 0 ${theme.spaces.x6};
`;

export const RevokeDialog = ({
  teamId,
  uid,
  teamName,
  onClose
}: {
  teamName: string;
  teamId: string;
  uid: string;
  onClose: () => void;
}) => {
  const { track } = useMixPanel();

  useEffect(() => {
    track(EntityEvents[Entity.Project].metadata.revokeOpen);
  }, [track]);

  const { mutateAsync, isLoading } = useGraphQlMutation(removeSubscriberFromProjectQuery);
  const queryClient = useQueryClient();
  const handleSubmit = async () => {
    await mutateAsync(
      {
        uid,
        teamId
      },
      {
        onError: (_error: ReactQueryError) => {
          onClose();
        },
        onSuccess: (resp) => {
          if (resp?.removeSubscriberFromProject === 1) {
            queryClient.invalidateQueries([Entity.Project]);
            track(EntityEvents[Entity.Project].metadata.revokeSuccess, { teamId, projectId: uid });
            toast.success({ content: 'Revoked successfully' });
          }
          onClose();
        }
      }
    );
  };
  return (
    <Dialog
      size='l'
      onOk={handleSubmit}
      okButton={{ children: 'Yes', disabled: isLoading }}
      onClose={onClose}
      open={true}
      title='Are you sure you want to revoke the access to team?'
      data-test-id='revoke-access-dialog'>
      <Stack gap={theme.spaces.x24} padding={`${theme.spaces.x24} 0`}>
        <Text level='sm'>
          Revoking access will prevent users within the
          <i>
            <TeamName>{teamName}</TeamName>
          </i>
          team from using the project in any of the future pipelines.
        </Text>
        <Text level='sm'>This action will not break any currently deployed pipelines.</Text>
      </Stack>
    </Dialog>
  );
};
